/**
 * InvoiceInbox — the store-level emailed-invoice workflow (formerly the
 * "Update Prices" page).
 *
 * Layout:
 *   1. Store invoice email card — the store's readable slug@domain address
 *      (legacy invoices+TOKEN@ alias still receives), processing mode,
 *      Copy / Upload Invoice / Settings actions.
 *   2. One-time banner when initial setup completes and the mode auto-switches
 *      from Automatic Setup to Review Before Adding.
 *   3. Invoice queue tabs: Needs Review · Completed · Not Processed.
 *   4. Review screen (per invoice): matched price updates are approved in
 *      place; genuinely new items continue to catalog assignment/templates.
 *
 * Data + actions come from the store-invoice-processor edge function
 * (get_inbox / get_invoice / approve / review_anyway / mark_not_invoice /
 * delete_email / set_mode / dismiss_banner). Manual invoice upload keeps the
 * existing client-side extract→match→approve flow (UpdatePrices.jsx).
 * Catalog browsing/editing lives in the Item Catalog (items.html).
 */

import React, { useCallback, useEffect, useState } from 'react';
import supabase from '../config/supabase';
import UploadInvoiceFlow from './UpdatePrices';
import TemplateOfferPrompt from './TemplateOfferPrompt';

const C = {
  text: '#5A4A42',
  muted: '#8B7B73',
  faint: '#A89890',
  border: '#F0E8E0',
  cardBorder: '#FAF6F2',
  bg: '#FEFCFA',
  chip: '#FAF6F2',
  green: '#6B8E6B',
  amber: '#B8860B',
  red: '#CD5C5C',
  purple: '#7B6B9A',
};

const card = {
  background: '#fff', borderRadius: 12, border: `1px solid ${C.cardBorder}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '1.5rem', marginBottom: '1.5rem',
};

const btn = (variant = 'primary') => ({
  padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem',
  cursor: 'pointer', whiteSpace: 'nowrap',
  ...(variant === 'primary'
    ? { border: 'none', background: C.text, color: '#fff' }
    : variant === 'danger'
      ? { border: `1px solid ${C.red}`, background: '#fff', color: C.red }
      : { border: '1px solid #D4C4B0', background: '#fff', color: C.text }),
});

const MODE_LABELS = {
  auto_setup: 'Automatic setup',
  review: 'Review before adding',
};
const MODE_DESCRIPTIONS = {
  auto_setup: 'Countertop reads the invoice and automatically creates or updates items, prices, categories, counting areas, and count templates.',
  review: 'Countertop reads the invoice and notifies you when it is ready. Nothing is added to your catalog until you approve it.',
};

const TABS = [
  { key: 'needs_review', label: 'Ready to Review' },
  { key: 'completed', label: 'Completed' },
  { key: 'not_processed', label: 'Couldn’t Process' },
  // Every recorded invoice, whatever brought it in — the queue tabs above only
  // know invoices that arrived as inbox messages.
  { key: 'invoices', label: 'View & Edit' },
];

function tabForStatus(status) {
  if (status === 'needs_review') return 'needs_review';
  // In-flight emails are omitted from the queue — processing is brief and the
  // interim state is not useful to operators. They reappear once ready/done.
  if (status === 'received' || status === 'processing') return null;
  if (status === 'processed' || status === 'partial') return 'completed';
  return 'not_processed'; // rejected | duplicate | failed
}

function statusLabel(row) {
  switch (row.status) {
    case 'received': return 'Receiving';
    case 'processing': return row.proposal ? 'Preparing updates' : 'Reading invoice';
    case 'needs_review': return 'Needs review';
    case 'processed':
    case 'partial': return 'Completed';
    case 'duplicate': return 'Possible duplicate invoice';
    case 'failed': return 'Failed';
    case 'rejected':
      // A gate rejection is not a recognition failure — labeling it "Not
      // recognized as invoice" (and showing no vendor) misleads users into
      // thinking the reader failed.
      return row.reject_reason === 'subscription_required' ? 'Needs subscription' : 'Not recognized as invoice';
    default: return 'Not recognized as invoice';
  }
}

// Human explanation for why a message wasn't processed, shown on the row so the
// user isn't left guessing (an unexplained rejected row reads as "the reader
// couldn't find the vendor").
const REJECT_EXPLANATIONS = {
  subscription_required: 'This arrived while the store had no active subscription, so it was not processed automatically. Use “Review Anyway” to process it now.',
  not_an_invoice: 'This didn’t look like a supplier invoice — it may be a price list, statement, quote, or order confirmation.',
  no_content: 'The email had no readable attachments or text.',
  user_marked_not_invoice: 'You marked this as not an invoice.',
};

function statusColor(status) {
  if (status === 'processed' || status === 'partial') return C.green;
  if (status === 'needs_review') return C.amber;
  if (status === 'failed' || status === 'rejected') return C.red;
  if (status === 'duplicate') return C.amber;
  return C.purple;
}

export async function getEdgeHeaders() {
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
        apikey: window.SUPABASE_KEY,
        'Content-Type': 'application/json',
      };
    }
  } catch (_) {}
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) {
        return {
          Authorization: `Bearer ${parsed.access_token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    } catch (_) {}
  }
  return null;
}

async function callProcessor(body) {
  const base = (typeof window !== 'undefined' && window.SUPABASE_URL) || 'https://hpmbxwbuoamijdyhpele.supabase.co';
  const headers = await getEdgeHeaders();
  if (!headers) throw new Error('Not signed in');
  const resp = await fetch(`${base}/functions/v1/store-invoice-processor`, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `Error ${resp.status}`);
  return data;
}

function fmtMoney(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

function fmtDate(v) {
  if (!v) return null;
  try {
    return new Date(v).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  } catch (_) { return String(v).slice(0, 10); }
}

// Compact date for the aligned row "date column" (e.g. "Jul 12").
function fmtDateShort(v) {
  if (!v) return null;
  try {
    return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (_) { return String(v).slice(0, 10); }
}

// Small spinner shown while a section's data is still loading, so an empty
// list never reads as "nothing here" before it has actually loaded.
function InlineLoader({ label = 'Loading…' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '2rem 1rem', color: C.muted }}>
      <style>{`@keyframes ctspin { to { transform: rotate(360deg); } }`}</style>
      <span aria-hidden="true" style={{
        width: 18, height: 18, borderRadius: '50%', display: 'inline-block',
        border: `2px solid ${C.border}`, borderTopColor: C.text, animation: 'ctspin 0.7s linear infinite',
      }} />
      <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function countsLine(counts) {
  if (!counts) return null;
  const bits = [];
  if (counts.lines) bits.push(`${counts.lines} line${counts.lines === 1 ? '' : 's'}`);
  if (counts.newItems) bits.push(`${counts.newItems} new item${counts.newItems === 1 ? '' : 's'}`);
  if (counts.priceUpdates) bits.push(`${counts.priceUpdates} price update${counts.priceUpdates === 1 ? '' : 's'}`);
  if (counts.needsAssignment) bits.push(`${counts.needsAssignment} item${counts.needsAssignment === 1 ? '' : 's'} need${counts.needsAssignment === 1 ? 's' : ''} assignment`);
  return bits.join(' · ') || null;
}

function resultCountsLine(result) {
  if (!result) return null;
  const bits = [];
  if (result.added) bits.push(`${result.added} item${result.added === 1 ? '' : 's'} added`);
  if (result.updated) bits.push(`${result.updated} price${result.updated === 1 ? '' : 's'} updated`);
  if (result.needsAssignment) bits.push(`${result.needsAssignment} item${result.needsAssignment === 1 ? '' : 's'} need${result.needsAssignment === 1 ? 's' : ''} assignment`);
  if (!bits.length && result.unchanged) bits.push(`${result.unchanged} unchanged`);
  return bits.join(' · ') || null;
}

// ── Modal shell ────────────────────────────────────────────────────────────────

function Modal({ children, onClose, maxWidth = 480 }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(60,45,38,0.45)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, maxWidth, width: '100%',
        maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Invoice Settings modal ────────────────────────────────────────────────────

function SettingsModal({ mode, onSave, onClose, saving }) {
  const [selected, setSelected] = useState(mode);
  return (
    <Modal onClose={saving ? undefined : onClose} maxWidth={520}>
      <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.15rem', marginBottom: '0.25rem' }}>
        Invoice handling
      </h3>
      <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: '1rem' }}>
        Choose how invoices sent to this store's invoice email are processed.
      </p>
      {['auto_setup', 'review'].map((m) => (
        <label key={m} style={{
          display: 'block', border: `2px solid ${selected === m ? C.text : C.border}`,
          borderRadius: 10, padding: '0.9rem 1rem', marginBottom: '0.75rem', cursor: 'pointer',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <input type="radio" name="invoice-mode" checked={selected === m} onChange={() => setSelected(m)} />
            <span style={{ fontWeight: 800, color: C.text }}>{MODE_LABELS[m]}</span>
          </div>
          <p style={{ color: C.muted, fontSize: '0.85rem', margin: 0 }}>{MODE_DESCRIPTIONS[m]}</p>
        </label>
      ))}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button disabled={saving} onClick={onClose} style={btn('secondary')}>Cancel</button>
        <button disabled={saving} onClick={() => onSave(selected)} style={{ ...btn('primary'), opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── Review screen ─────────────────────────────────────────────────────────────

// Invoice money summary fields, in display order. Kept separate from item cost:
// tax / delivery / other are the invoice's own charges, not part of any item.
const TOTAL_FIELDS = [
  ['subtotal', 'Subtotal'],
  ['tax', 'Tax'],
  ['delivery', 'Delivery'],
  ['other_charges', 'Other charges'],
  ['discount', 'Discount'],
  ['total', 'Invoice total'],
];

// ── Additional charges (itemized non-product lines) ──────────────────────────
// The frozen charge taxonomy shared with the processor. Wire shape everywhere
// is { type, label, amount } — amounts positive as printed, credits are
// positive numbers with type 'credit'.
const INVOICE_CHARGE_TYPES = ['sales_tax', 'delivery', 'fuel_surcharge', 'minimum_order_fee', 'deposit', 'credit', 'other'];

const CHARGE_TYPE_LABELS = {
  sales_tax: 'Sales tax',
  delivery: 'Delivery',
  fuel_surcharge: 'Fuel surcharge',
  minimum_order_fee: 'Minimum order fee',
  deposit: 'Deposit',
  credit: 'Credit',
  other: 'Other',
};

// Friendly names for the vendor fee columns a proposal's vendorFeePlan can fill.
const FEE_COLUMN_LABELS = {
  delivery_fee: 'Delivery fee',
  fuel_surcharge_amount: 'Fuel surcharge',
  below_minimum_fee: 'Small-order fee',
  sales_tax_rate: 'Sales tax rate',
};

// Old stored proposals predate `charges` — tolerate missing/garbage shapes and
// coerce to editable rows (unknown type → 'other', amount kept editable).
function normalizeProposalCharges(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c) => c && typeof c === 'object')
    .map((c) => ({
      type: INVOICE_CHARGE_TYPES.includes(c.type) ? c.type : 'other',
      label: c.label != null ? String(c.label) : '',
      // NULL means unknown, never 0 — Number(null) is 0, so guard nullish/blank
      // first; '' renders the blank input and approves back as amount null.
      amount: c.amount == null || c.amount === '' ? ''
        : (Number.isFinite(Number(c.amount)) ? Number(c.amount) : ''),
    }));
}

// Old stored proposals predate `vendorFeePlan`; keep only well-formed entries.
function normalizeVendorFeePlan(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((e) => e && typeof e === 'object'
    && typeof e.column === 'string' && e.column !== ''
    && Number.isFinite(Number(e.value)));
}

// sales_tax_rate is stored as a fraction (0.0825) but read as a percent.
function feePlanValueText(entry) {
  const n = Number(entry?.value);
  if (!Number.isFinite(n)) return '—';
  if (entry.column === 'sales_tax_rate') return `${Math.round(n * 10000) / 100}%`;
  return fmtMoney(n);
}

function feePlanEntryLabel(entry) {
  return FEE_COLUMN_LABELS[entry.column] || String(entry.column).replace(/_/g, ' ');
}

// Client-side copy of the processor's explainTotalsReconciliation — same math:
// diff = printed total − computed line total (null when either is missing);
// feeSum = (tax ?? 0) + (delivery ?? 0) + (other_charges ?? 0) − (discount ?? 0),
// null when every component is null; explained when |diff − feeSum| <= $0.02.
function explainTotalsReconciliation({ totals, computedLineTotal }) {
  const money = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
  const total = money(totals ? totals.total : null);
  const lineTotal = money(computedLineTotal);
  const diff = total != null && lineTotal != null ? Math.round((total - lineTotal) * 100) / 100 : null;
  const tax = money(totals ? totals.tax : null);
  const delivery = money(totals ? totals.delivery : null);
  const other = money(totals ? totals.other_charges : null);
  const discount = money(totals ? totals.discount : null);
  const feeSum = (tax == null && delivery == null && other == null && discount == null)
    ? null
    : Math.round(((tax ?? 0) + (delivery ?? 0) + (other ?? 0) - (discount ?? 0)) * 100) / 100;
  const explained = diff != null && feeSum != null && Math.abs(diff - feeSum) <= 0.02;
  return { diff, feeSum, explained };
}

// Per-line taxability evidenced on the invoice. Rendered only for
// taxable === true (null = not indicated → no pill). Read-only in v1.
function TaxedPill({ style }) {
  return (
    <span style={{
      display: 'inline-block', color: '#6B5A52', background: '#F5F0EC', border: '1px solid #E4D9CE',
      borderRadius: 999, padding: '0.14rem 0.5rem', fontSize: '0.64rem', fontWeight: 800,
      verticalAlign: 'middle', ...style,
    }}>
      Taxed
    </span>
  );
}

function priceChangeLabel(oldValue, newValue) {
  const oldPrice = Number(oldValue);
  const newPrice = Number(newValue);
  if (!Number.isFinite(oldPrice) || oldPrice <= 0) return 'First recorded price';
  if (!Number.isFinite(newPrice)) return 'No change';
  const delta = newPrice - oldPrice;
  if (Math.abs(delta) < 0.005) return 'No change';
  const sign = delta > 0 ? '+' : '−';
  const pct = (Math.abs(delta) / oldPrice) * 100;
  return `${sign}$${Math.abs(delta).toFixed(2)} · ${sign}${pct.toFixed(1)}%`;
}

function isMatchedLine(line) {
  return !!(line.existingItemId || line.classification === 'unchanged' || line.classification === 'price_change');
}

function isNewLine(line) {
  return !line.existingItemId && line.classification === 'new';
}

// ── Cross-reference link (same product, other vendor/size) ───────────────────
// A "new" invoice line can carry a cross_reference_match from the processor:
// deterministic candidates for an EXISTING catalog item the line could attach
// to as another purchase option instead of creating a duplicate. With no user
// interaction nothing is pinned and the backend's own routing decides (default
// behavior is unchanged); pinning a candidate sends matchItemId on approve
// (the line attaches on approval), and "keep as new" sends override:'new'.

function xrefCandidateList(xref) {
  if (!xref || typeof xref !== 'object') return [];
  const coerce = (c) => {
    if (!c || c.item_id == null || c.item_id === '') return null;
    const score = Number(c.score);
    return {
      item_id: String(c.item_id),
      description: c.description != null ? String(c.description) : null,
      score: Number.isFinite(score) ? score : null,
      reason: c.reason != null ? String(c.reason) : null,
      decision: c.decision === 'attach' ? 'attach' : 'suggest',
    };
  };
  const list = (Array.isArray(xref.candidates) ? xref.candidates : []).map(coerce).filter(Boolean);
  if (list.length) return list.slice(0, 3);
  const top = coerce({ item_id: xref.item_id, description: xref.description, score: xref.score, decision: xref.route });
  return top ? [top] : [];
}

// Scores arrive as a 0–1 fraction (0.92 → "92%"); tolerate an already-percent
// value so a shape drift never renders "9200%".
function xrefScorePct(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return Math.round(n <= 1 ? n * 100 : n);
}

function candidateLabel(c) {
  const pct = xrefScorePct(c.score);
  return `${c.description || 'an existing item'}${pct != null ? ` (${pct}%)` : ''}`;
}

/**
 * Link affordance under a new invoice line that cross-referenced an existing
 * item: which item it will link to, a switcher when there is more than one
 * candidate, and pin / force-new actions. `line._linkMode` is null (backend
 * default), 'pin' (send matchItemId), or 'new' (send override:'new').
 */
function CrossReferenceLinkControl({ line, onChange }) {
  const candidates = line._xrefCandidates || [];
  if (!candidates.length) return null;
  const selected = candidates.find((c) => c.item_id === line._linkCandidateId) || candidates[0];
  const mode = line._linkMode || null;
  const isAttachRoute = line.cross_reference_match?.route === 'attach';
  return (
    <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#F6F3FB', border: '1px solid #DCD2EE', borderRadius: 8 }}>
      <div style={{ color: '#5E5080', fontSize: '0.72rem', fontWeight: 800 }}>
        {isAttachRoute ? 'Links to an existing item' : 'Possible link to an existing item'}
      </div>
      <div style={{ color: '#6B5A52', fontSize: '0.72rem', marginTop: '0.2rem', lineHeight: 1.4 }}>
        {isAttachRoute ? 'Links to' : 'Suggested link'} <strong style={{ color: '#5A4A42' }}>{selected.description || 'an existing item'}</strong>
        {xrefScorePct(selected.score) != null ? ` (${xrefScorePct(selected.score)}%)` : ''}
        {selected.reason ? <span style={{ color: C.faint }}>{` · ${selected.reason.replace(/_/g, ' ')}`}</span> : null}
      </div>
      {candidates.length > 1 && (
        <select
          value={selected.item_id}
          onChange={(e) => onChange({ _linkCandidateId: e.target.value })}
          aria-label={`Choose which existing item ${line.name || 'this line'} links to`}
          style={{
            marginTop: '0.35rem', width: '100%', border: '1px solid #DCD2EE', borderRadius: 7,
            padding: '0.3rem 0.4rem', fontSize: '0.74rem', color: C.text, background: '#fff',
          }}
        >
          {candidates.map((c) => (
            <option key={c.item_id} value={c.item_id}>{candidateLabel(c)}</option>
          ))}
        </select>
      )}
      {mode === null ? (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
          <button
            type="button"
            onClick={() => onChange({ _linkMode: 'pin', _linkCandidateId: selected.item_id })}
            style={{ ...btn('secondary'), padding: '0.3rem 0.6rem', fontSize: '0.72rem' }}
          >
            Link to this item
          </button>
          <button
            type="button"
            onClick={() => onChange({ _linkMode: 'new' })}
            style={{ ...btn('secondary'), padding: '0.3rem 0.6rem', fontSize: '0.72rem' }}
          >
            Keep as new item
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
          <span style={{
            display: 'inline-block', borderRadius: 999, padding: '0.22rem 0.55rem', fontSize: '0.68rem', fontWeight: 800,
            ...(mode === 'pin'
              ? { color: '#2F5D3A', background: '#E3F0E4', border: '1px solid #B9DBBE' }
              : { color: '#7A5A0A', background: '#FFF0BD', border: '1px solid #E6CD83' }),
          }}>
            {mode === 'pin' ? `✓ Will link to ${selected.description || 'the selected item'}` : 'Will be added as a separate item'}
          </span>
          <button
            type="button"
            onClick={() => onChange({ _linkMode: null })}
            style={{ background: 'none', border: 'none', padding: 0, color: C.muted, fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

// Per-line accept/ignore for this review only. Default is accepted; ignore is
// not remembered for future invoices — only sent as override:'skip' on approve.
function SectionSelectControls({ onSelectAll, onDeselectAll, acceptedCount, totalCount, label }) {
  if (totalCount === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', marginTop: '0.55rem' }}>
      <span style={{ color: '#6B5A52', fontSize: '0.74rem', fontWeight: 700 }}>
        {acceptedCount} of {totalCount} accepted
      </span>
      <button type="button" onClick={onSelectAll} style={{ background: 'none', border: 'none', padding: 0, color: C.text, fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', textDecoration: 'underline' }}>
        Select all
      </button>
      <button type="button" onClick={onDeselectAll} style={{ background: 'none', border: 'none', padding: 0, color: C.muted, fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', textDecoration: 'underline' }}>
        Deselect all
      </button>
      <span style={{ color: C.faint, fontSize: '0.7rem' }}>{label}</span>
    </div>
  );
}

function AcceptToggle({ accepted, onChange, ariaLabel }) {
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }} role="group" aria-label={ariaLabel}>
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={accepted}
        style={{
          border: 'none', padding: '0.35rem 0.65rem', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
          background: accepted ? C.text : 'transparent', color: accepted ? '#fff' : C.muted,
        }}
      >
        Accept
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={!accepted}
        style={{
          border: 'none', borderLeft: `1px solid ${C.border}`, padding: '0.35rem 0.65rem', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
          background: !accepted ? '#F5EAEA' : 'transparent', color: !accepted ? C.red : C.muted,
        }}
      >
        Ignore
      </button>
    </div>
  );
}

// Contact fields the invoice reader can capture, in display order. Only BLANK
// fields on the vendor are filled at apply time (existing values are kept), but
// everything captured is shown here so the user sees what will be added.
const VENDOR_CONTACT_FIELDS = [
  ['legal_name', 'Legal name'],
  ['contact_name', 'Contact'],
  ['order_email', 'Order email'],
  ['phone', 'Phone'],
  ['address', 'Address'],
  ['payment_terms', 'Terms'],
  ['account_number', 'Account #'],
];

/**
 * The invoice's issuing (supplier) vendor: whether it matched an existing vendor
 * or will be created new, the contact details read off the invoice, and a picker
 * to confirm or change the match. Surfaces what the processor resolves server-
 * side so the user can correct a wrong match before the vendor record is touched.
 */
function VendorCard({ resolution, contact, vendorList, choice, onChange, candidateName }) {
  const supplier = (resolution?.candidateName || candidateName || '').trim();
  const contactRows = VENDOR_CONTACT_FIELDS
    .map(([key, label]) => [label, contact ? contact[key] : null])
    .filter(([, value]) => value != null && String(value).trim() !== '');
  const hasChoices = Array.isArray(vendorList) && vendorList.length > 0;
  const selectValue = choice?.mode === 'existing' && choice.vendorId ? String(choice.vendorId) : 'new';
  const matchedExisting = choice?.mode === 'existing' && choice.vendorId;
  const aiConfidence = resolution?.source === 'ai' && resolution?.confidencePct != null
    ? resolution.confidencePct : null;
  // Was this match the processor's own suggestion (vs. a user change)?
  const isSuggestedMatch = resolution?.status === 'matched'
    && String(resolution.vendorId || '') === (choice?.vendorId || '');

  return (
    <section style={{ ...card, marginBottom: '1.5rem' }} aria-labelledby="invoice-vendor-heading">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h4 id="invoice-vendor-heading" style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '1rem', fontWeight: 800, margin: 0 }}>Vendor</h4>
        {supplier && <span style={{ color: '#6B5A52', fontSize: '0.8rem' }}>read as “{supplier}”</span>}
      </div>

      <div style={{ marginTop: '0.55rem', display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
        {matchedExisting ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#2F5D3A', background: '#E3F0E4', border: '1px solid #B9DBBE', borderRadius: 999, padding: '0.25rem 0.6rem', fontSize: '0.74rem', fontWeight: 800 }}>
            ✓ {isSuggestedMatch ? 'Matched to existing vendor' : 'Using existing vendor'}
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#7A5A0A', background: '#FFF0BD', border: '1px solid #E6CD83', borderRadius: 999, padding: '0.25rem 0.6rem', fontSize: '0.74rem', fontWeight: 800 }}>
            New vendor — will be created
          </span>
        )}
        {matchedExisting && aiConfidence != null && (
          <span style={{ color: '#6B5A52', fontSize: '0.72rem' }}>{aiConfidence}% confident</span>
        )}
      </div>

      {hasChoices && (
        <label style={{ display: 'block', marginTop: '0.75rem', maxWidth: 420 }}>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.25rem' }}>Vendor for this invoice</span>
          <select
            value={selectValue}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v === 'new' ? { mode: 'new' } : { mode: 'existing', vendorId: v });
            }}
            style={{ width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '0.5rem 0.5rem', fontSize: '0.86rem', color: C.text, background: '#fff' }}
          >
            <option value="new">{supplier ? `Create new vendor “${supplier}”` : 'Create a new vendor'}</option>
            {vendorList.map((v) => (
              <option key={v.id} value={String(v.id)}>{v.displayName || 'Unnamed vendor'}</option>
            ))}
          </select>
          <span style={{ display: 'block', color: '#8B7B73', fontSize: '0.72rem', marginTop: '0.3rem', lineHeight: 1.4 }}>
            {matchedExisting
              ? 'Pick “Create new” if this is a different supplier. The invoice’s name is remembered as an alias so it matches automatically next time.'
              : 'Choose an existing vendor if this invoice is really from one you already have.'}
          </span>
        </label>
      )}

      {contactRows.length > 0 && (
        <div style={{ marginTop: '0.9rem', background: '#FCFAF8', border: '1px solid #EFE7E0', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ color: C.text, fontSize: '0.8rem', fontWeight: 800, marginBottom: '0.4rem' }}>Contact details from this invoice</div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 0.75rem', margin: 0 }}>
            {contactRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt style={{ color: '#8B7B73', fontSize: '0.74rem', fontWeight: 700 }}>{label}</dt>
                <dd style={{ color: C.text, fontSize: '0.78rem', margin: 0, minWidth: 0, wordBreak: 'break-word' }}>{String(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
          <div style={{ color: '#8B7B73', fontSize: '0.72rem', marginTop: '0.5rem', lineHeight: 1.4 }}>
            These fill only the blank fields on the vendor’s record — anything you already entered is kept.
          </div>
        </div>
      )}
    </section>
  );
}

function ReviewScreen({ emailId, onBack, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(null);
  const [completion, setCompletion] = useState(null);
  // Invoice money summary (subtotal/tax/delivery/other/discount/total) captured
  // from the invoice, editable before approval; computedLineTotal is our sum of
  // the item lines, shown for reconciliation against the printed total.
  const [totals, setTotals] = useState(null);
  const [computedLineTotal, setComputedLineTotal] = useState(null);
  // Itemized non-product charges (tax/delivery/fuel/…) read off the invoice,
  // editable before approval. Old proposals lack the key → empty list.
  const [charges, setCharges] = useState([]);
  // The processor's fill-only-blank vendor fee plan and the user's per-entry
  // accept checkboxes (default checked). Old proposals lack the key → empty.
  const [vendorFeePlan, setVendorFeePlan] = useState([]);
  const [feeAccepted, setFeeAccepted] = useState([]);
  // New-item IDs for the opt-in "create templates?" prompt after an
  // "Add Items Only" approval — same flow as the Item Catalog's Add Items.
  const [templateOffer, setTemplateOffer] = useState(null);
  // Issuing-vendor resolution the processor computed (matched existing vs. new),
  // the supplier contact it read off the invoice, the store's vendors for the
  // "change match" picker, and the user's confirmed choice sent on approve.
  const [vendorResolution, setVendorResolution] = useState(null);
  const [vendorContact, setVendorContact] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorChoice, setVendorChoice] = useState(null);
  // Invoice date, editable before approval. The extracted value seeds it; an
  // invoice whose date couldn't be read is held in review precisely so the
  // reviewer can supply one — the invoice's own date (not when it was read)
  // is what proves a price is newer than the one on file. Without a date,
  // approving records the purchase and leaves every catalog price alone.
  const [invoiceDate, setInvoiceDate] = useState('');
  const [extractedInvoiceDate, setExtractedInvoiceDate] = useState(null);
  // "Update prices under this date?" confirmation, shown only when the
  // reviewer set or changed the date and price updates are in play.
  const [datePrompt, setDatePrompt] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await callProcessor({ action: 'get_invoice', emailId });
        if (!alive) return;
        setInvoice(data.invoice);
        const items = data.invoice?.proposal?.items || [];
        // accepted defaults true for every line on this review only — not persisted.
        setLines(items.map((it) => {
          const xrefCandidates = xrefCandidateList(it.cross_reference_match);
          return {
            ...it,
            action: it.classification,
            accepted: true,
            price: it.price,
            // Cross-reference link state: candidate default = top, mode null =
            // no pin — approving without touching the control behaves exactly
            // as before (the backend's own routing decides).
            _xrefCandidates: xrefCandidates,
            _linkCandidateId: xrefCandidates[0]?.item_id ?? null,
            _linkMode: null,
          };
        }));
        setTotals(data.invoice?.proposal?.totals || null);
        setComputedLineTotal(data.invoice?.proposal?.computedLineTotal ?? null);
        const extractedDate = data.invoice?.invoice_date ? String(data.invoice.invoice_date).slice(0, 10) : '';
        setInvoiceDate(extractedDate);
        setExtractedInvoiceDate(extractedDate || null);
        setCharges(normalizeProposalCharges(data.invoice?.proposal?.charges));
        const feePlan = normalizeVendorFeePlan(data.invoice?.proposal?.vendorFeePlan);
        setVendorFeePlan(feePlan);
        setFeeAccepted(feePlan.map(() => true));
        const resolution = data.invoice?.proposal?.vendorResolution || null;
        setVendorResolution(resolution);
        setVendorContact(data.invoice?.proposal?.vendorContact || null);
        setVendorList(Array.isArray(data.vendors) ? data.vendors : []);
        // Seed the confirmable choice from what the processor decided: keep the
        // matched vendor, or "create new" when nothing matched.
        setVendorChoice(
          resolution?.status === 'matched' && resolution.vendorId
            ? { mode: 'existing', vendorId: String(resolution.vendorId) }
            : { mode: 'new' },
        );
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [emailId]);

  const setLineAccepted = (idx, accepted) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      return {
        ...l,
        accepted,
        // skip = drop this line for this approval only (processor already supports it).
        action: accepted ? l.classification : 'skip',
      };
    }));
  };

  const setSectionAccepted = (predicate, accepted) => {
    setLines((prev) => prev.map((l) => {
      if (!predicate(l)) return l;
      return {
        ...l,
        accepted,
        action: accepted ? l.classification : 'skip',
      };
    }));
  };

  // Accept an AI-suggested potential match: turn the new line into a price
  // change against the existing item. `mapped` flags it so approve sends
  // override:'price_change' (the deterministic re-check at apply can't re-derive
  // a sub-threshold vendor match on its own).
  const mapPotentialMatch = (idx) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx || !l.potentialMatch) return l;
      const accepted = l.accepted !== false;
      return {
        ...l,
        classification: 'price_change',
        action: accepted ? 'price_change' : 'skip',
        existingItemId: l.potentialMatch.existingItemId,
        existingDescription: l.potentialMatch.existingDescription,
        oldPrice: l.potentialMatch.oldPrice,
        mapped: true,
        potentialMatch: null,
      };
    }));
  };

  const setTotalField = (field, raw) => {
    const n = parseFloat(String(raw).replace(/[$,\s]/g, ''));
    const value = Number.isFinite(n) ? n : null;
    setTotals((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const setChargeField = (idx, key, value) => {
    setCharges((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c)));
  };
  const addCharge = () => setCharges((prev) => [...prev, { type: 'other', label: '', amount: '' }]);
  const removeCharge = (idx) => setCharges((prev) => prev.filter((_, i) => i !== idx));
  const toggleFeeAccepted = (idx) => {
    setFeeAccepted((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  // Cross-reference link choice on a new line: { _linkCandidateId } switches
  // the pinned candidate, { _linkMode: 'pin' | 'new' | null } pins the link,
  // forces a separate new item, or returns to the backend default.
  const setLineLink = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  // Approve entry point: when the reviewer SET or CHANGED the invoice date and
  // price updates are on the table, ask whether matched items should take this
  // invoice's prices under that date before anything is written. An unchanged
  // extracted date keeps the direct path; no date at all approves record-only.
  const approve = () => {
    const dateEdited = (invoiceDate || null) !== (extractedInvoiceDate || null);
    const pendingPriceUpdates = lines.filter(
      (l) => l.accepted !== false && l.classification === 'price_change',
    ).length;
    if (invoiceDate && dateEdited && pendingPriceUpdates > 0) {
      setDatePrompt(true);
      return;
    }
    submitApproval(Boolean(invoiceDate));
  };

  const submitApproval = async (applyPriceUpdates) => {
    const method = 'items_only';
    setBusy('approve');
    try {
      const items = lines.map((l) => {
        const accepted = l.accepted !== false;
        const action = accepted ? l.classification : 'skip';
        return {
          name: l.name,
          vendor: l.vendor,
          unit: l.unit,
          price: l.price,
          // Suggested category (e.g. "Beer") carried back so it is applied to the
          // new item instead of being re-guessed from the name alone.
          category: l.category ?? null,
          // Purchase quantities MUST be carried back — they are what the invoice's
          // line records and the purchases-by-category (COGS) totals are built on.
          // Omitting them persists null amounts on the default review path.
          quantity: l.quantity ?? null,
          extendedAmount: l.extendedAmount ?? null,
          // Tri-state per-line taxability from the invoice rides back unchanged
          // (old proposals without the key send null — never fabricated).
          taxable: l.taxable ?? null,
          // Line evidence the reader printed beside the price (vendor SKU,
          // pack, brand, printed U/M) rides back too — it is what the purchase
          // option is written from. Old proposals lack the keys and send null.
          brand: l.brand ?? null,
          sku: l.sku ?? null,
          packSize: l.packSize ?? null,
          printedUom: l.printedUom ?? null,
          existingItemId: l.existingItemId,
          // override:'skip' drops the line for this approval only; not remembered later.
          // A user-mapped potential match (l.mapped) must send override:'price_change'
          // so the apply-time re-check keeps it instead of re-deriving "new".
          // A cross-reference forced to "keep as new" sends override:'new' so the
          // backend creates a separate item instead of attaching/linking.
          override: accepted && l._linkMode === 'new'
            ? 'new'
            : (action !== l.classification ? action : (l.mapped ? 'price_change' : null)),
          // A pinned cross-reference candidate on a still-new line attaches the
          // line to that item as a purchase option on approve (no duplicate).
          matchItemId: action === 'price_change'
            ? l.existingItemId
            : (accepted && l._linkMode === 'pin' ? (l._linkCandidateId || null) : null),
        };
      });
      // Like quantity/extendedAmount above: charges and vendorFees must
      // actually ride the POST — the processor records only what arrives in
      // the body. charges is the user-edited list (empty array = user removed
      // all); vendorFees is the accepted subset of the stored fee plan.
      const chargesBody = charges.map((c) => {
        const n = parseFloat(c.amount);
        return {
          type: INVOICE_CHARGE_TYPES.includes(c.type) ? c.type : 'other',
          label: c.label != null ? String(c.label) : '',
          amount: Number.isFinite(n) ? Math.round(n * 100) / 100 : null,
        };
      });
      const vendorFees = vendorFeePlan
        .filter((_, i) => feeAccepted[i] !== false)
        .map((e) => ({ column: e.column, value: e.value }));
      const data = await callProcessor({
        action: 'approve', emailId, method, items, totals, vendor: vendorChoice, charges: chargesBody, vendorFees,
        // The reviewed invoice date (may correct or supply the extracted one)
        // and whether matched items take this invoice's prices under it. The
        // server re-derives record-only when the date is absent either way.
        invoice_date: invoiceDate || null,
        apply_price_updates: applyPriceUpdates !== false,
      });
      const addedItemIds = Array.isArray(data.result?.addedItemIds) ? data.result.addedItemIds.filter(Boolean) : [];
      setCompletion({ summary: data.summary, result: data.result, addedItemIds });
      if (addedItemIds.length > 0) {
        setTemplateOffer(addedItemIds);
      } else {
        onApplied?.();
        onBack();
      }
    } catch (err) {
      alert(`Could not approve this invoice: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div style={card}><p style={{ color: C.muted }}>Loading invoice…</p></div>;
  if (error) return (
    <div style={card}>
      <p style={{ color: C.red }}>Couldn't load this invoice: {error}</p>
      <button onClick={onBack} style={btn('secondary')}>← Back to Invoice Inbox</button>
    </div>
  );

  const inv = invoice || {};
  const vendor = inv.vendor_name || 'Vendor not identified';
  const matchedLines = lines.filter(isMatchedLine);
  const newLines = lines.filter(isNewLine);
  const acceptedMatched = matchedLines.filter((line) => line.accepted !== false);
  const acceptedNew = newLines.filter((line) => line.accepted !== false);
  const priceUpdateCount = acceptedMatched.filter((line) => line.classification === 'price_change').length;
  const printedTotal = totals && totals.total != null ? Number(totals.total) : null;
  const reconcileDiff = (printedTotal != null && computedLineTotal != null) ? printedTotal - computedLineTotal : null;
  const showReconcile = reconcileDiff != null && Math.abs(reconcileDiff) > 0.01;
  // When the captured tax/delivery/other/discount figures account for the gap
  // (within $0.02), attribute it to the detected charges instead of the
  // generic "this is normal" note.
  const reconcile = explainTotalsReconciliation({ totals, computedLineTotal });
  const namedDetectedCharges = charges
    .map((c) => ({ label: (c.label || '').trim(), type: c.type, n: Number(c.amount) }))
    .filter((c) => Number.isFinite(c.n) && c.n !== 0)
    .map((c) => `${c.label || CHARGE_TYPE_LABELS[c.type] || 'Other'} ${fmtMoney(Math.abs(c.n))}`);
  const namedTotalsComponents = [['tax', 'Tax'], ['delivery', 'Delivery'], ['other_charges', 'Other charges'], ['discount', 'Discount']]
    .filter(([k]) => totals && totals[k] != null && Number(totals[k]) !== 0)
    .map(([k, label]) => `${label} ${fmtMoney(Math.abs(Number(totals[k])))}`);
  const explainedChargeNames = (namedDetectedCharges.length ? namedDetectedCharges : namedTotalsComponents).join(', ');

  if (completion) {
    const needs = completion.result?.needsAssignment || 0;
    const addedIds = completion.addedItemIds || [];
    const updated = Number(completion.result?.updated) || 0;
    const added = Number(completion.result?.added) || 0;
    const totalChanged = updated + added;
    // Lines that linked to an existing item as another purchase option instead
    // of creating a duplicate — grouped under the canonical item name with the
    // invoice's own line text muted beneath ("Sysco · Whl Milk · 4 × 1 gal").
    const attachedDetails = Array.isArray(completion.result?.attachedDetails)
      ? completion.result.attachedDetails
      : [];
    return (
      <div style={card}>
        <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.2rem', marginBottom: '0.35rem' }}>
          {needs > 0 ? 'Price updates saved' : '✓ Invoice approved'}
        </h3>
        <p style={{ color: C.text, marginBottom: '1rem' }}>
          {needs > 0
            ? `${updated} matched item${updated === 1 ? '' : 's'} updated. ${added} new item${added === 1 ? '' : 's'} still need${added === 1 ? 's' : ''} a next step.`
            : completion.summary}
        </p>
        {attachedDetails.length > 0 && (
          <div style={{ marginBottom: '1rem', background: '#FCFAF8', border: '1px solid #EFE7E0', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
            <div style={{ color: C.text, fontSize: '0.85rem', fontWeight: 800, marginBottom: '0.4rem' }}>
              {attachedDetails.length} line{attachedDetails.length === 1 ? '' : 's'} attached to existing items
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {attachedDetails.map((d, i) => {
                const src = lines.find((l) => (l.name || '') === (d.description || '') && (l.vendor || '') === (d.vendor || ''));
                const qtyUnit = src && src.quantity != null && src.unit
                  ? `${src.quantity} × ${src.unit}`
                  : (d.unit || null);
                return (
                  <li key={i} style={{ padding: '0.3rem 0', borderTop: i === 0 ? 'none' : '1px dashed #EFE7E0' }}>
                    <div style={{ fontWeight: 700, color: C.text, fontSize: '0.84rem' }}>{d.matchedItem || d.description || '—'}</div>
                    <div style={{ color: C.muted, fontSize: '0.74rem', marginTop: '0.1rem' }}>
                      {[d.vendor, d.description, qtyUnit].filter(Boolean).join(' · ')}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => { onApplied?.(); onBack(); }} style={btn('secondary')}>Return to Invoice Inbox</button>
        </div>
        {templateOffer && (
          <TemplateOfferPrompt
            itemIds={templateOffer}
            onClose={() => { setTemplateOffer(null); onApplied?.(); onBack(); }}
            title="Choose what happens next"
            dismissLabel="Return to Invoice Inbox"
            confirmLabel="Countertop create templates"
            introText={(
              <>
                This invoice processed <strong>{totalChanged}</strong> item{totalChanged === 1 ? '' : 's'}.{' '}
                <strong>{updated}</strong> matched item{updated === 1 ? ' was' : 's were'} used to update prices, and{' '}
                <strong>{added}</strong> item{added === 1 ? ' is' : 's are'} new.
              </>
            )}
            helperText="Choose whether to return to the invoice inbox, or let Countertop prepare count templates from the new items."
          />
        )}
      </div>
    );
  }

  return (
    <div className="invoice-review-page">
      <style>{`
        .invoice-review-approval { width: auto; min-width: 190px; }
        .invoice-review-table-head,
        .invoice-review-matched-row { display: grid; grid-template-columns: minmax(220px, 2fr) 100px 100px 130px 150px; gap: 0.75rem; align-items: center; }
        .invoice-review-table-head { padding: 0 0.85rem 0.5rem; color: #6B5A52; font-size: 0.72rem; font-weight: 700; }
        .invoice-review-matched-row { padding: 0.85rem; border-top: 1px solid #EFE7E0; }
        .invoice-review-mobile-label { display: none; }
        .invoice-review-charge-row { display: grid; grid-template-columns: minmax(130px, 170px) minmax(140px, 1fr) 130px 28px; gap: 0.5rem; align-items: center; margin-top: 0.45rem; }
        @media (max-width: 768px) {
          .invoice-review-approval { width: 100%; }
          .invoice-review-table-head { display: none; }
          .invoice-review-matched-row { display: block; margin: 0.65rem; padding: 0.8rem; border: 1px solid #E8DDD0; border-radius: 10px; background: #fff; }
          .invoice-review-cell { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 0.5rem; align-items: center; margin-top: 0.55rem; }
          .invoice-review-item-cell { display: block; margin-top: 0; }
          .invoice-review-mobile-label { display: block; color: #6B5A52; font-size: 0.7rem; font-weight: 700; }
          .invoice-review-charge-row { grid-template-columns: minmax(0, 1fr) 110px 28px; }
          .invoice-review-charge-row > select { grid-column: 1 / -1; }
        }
      `}</style>
      <div style={card}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.75rem' }}>
          ← Back to Invoice Inbox
        </button>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.25rem', margin: 0 }}>{vendor}</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem 0.9rem', color: '#6B5A52', fontSize: '0.8rem', marginTop: '0.35rem' }}>
            {inv.invoice_number && <span>Invoice #{inv.invoice_number}</span>}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600 }}>
              Invoice date
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                aria-label="Invoice date"
                style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '0.3rem 0.45rem', color: C.text, fontSize: '0.8rem', fontFamily: 'inherit' }}
              />
            </label>
            {inv.received_at && <span>Received: {fmtDate(inv.received_at)}</span>}
          </div>
          {!invoiceDate && (
            <div style={{ background: '#FFF7E6', border: '1px solid #F0DBA8', color: '#7A5A0A', borderRadius: 10, padding: '0.6rem 0.8rem', fontSize: '0.8rem', lineHeight: 1.5, marginTop: '0.6rem' }}>
              <strong>Invoice date was not found.</strong> Set it above to let this invoice update
              prices — a price only replaces the one on file when its invoice is dated more
              recently. Approving without a date records the purchase and changes no prices.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.75rem' }}>
            <span style={{ background: '#F5F0EC', color: C.text, borderRadius: 999, padding: '0.25rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>{matchedLines.length} existing item{matchedLines.length === 1 ? '' : 's'}</span>
            <span style={{ background: '#EAF5E8', color: '#2F6B32', borderRadius: 999, padding: '0.25rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>{priceUpdateCount} price update{priceUpdateCount === 1 ? '' : 's'} accepted</span>
            {newLines.length > 0 && <span style={{ background: '#FFF2CC', color: '#7A5A0A', borderRadius: 999, padding: '0.25rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>{acceptedNew.length} of {newLines.length} new item{newLines.length === 1 ? '' : 's'} accepted</span>}
          </div>
          <p style={{ color: C.text, fontSize: '0.84rem', margin: '0.75rem 0 0', lineHeight: 1.45 }}>
            <strong>{matchedLines.length} existing item{matchedLines.length === 1 ? '' : 's'} from this invoice.</strong>{' '}
            Accept or ignore individual lines for this review only, then check totals and approve.
          </p>
        </div>
      </div>

      {vendorResolution && (
        <VendorCard
          resolution={vendorResolution}
          contact={vendorContact}
          vendorList={vendorList}
          choice={vendorChoice}
          onChange={setVendorChoice}
          candidateName={inv.vendor_name}
        />
      )}

      <div style={{ ...card, padding: '1rem 1rem 0.9rem' }}>
        <p style={{ color: C.text, fontSize: '0.86rem', margin: 0, lineHeight: 1.55 }}>
          All lines are accepted by default. Ignore any you don’t want applied this time — choices are only for this invoice, not future ones.
        </p>
      </div>

      <section style={{ ...card, padding: 0, overflow: 'hidden' }} aria-labelledby="matched-items-heading">
        <div style={{ padding: '1rem 1rem 0.85rem', background: '#FCFAF8' }}>
          <h4 id="matched-items-heading" style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '1rem', fontWeight: 800, margin: 0 }}>Existing items from invoice</h4>
          <p style={{ color: '#6B5A52', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>These invoice lines match items already in your catalog.</p>
          <SectionSelectControls
            acceptedCount={acceptedMatched.length}
            totalCount={matchedLines.length}
            onSelectAll={() => setSectionAccepted(isMatchedLine, true)}
            onDeselectAll={() => setSectionAccepted(isMatchedLine, false)}
            label="Ignored lines keep their current catalog price."
          />
        </div>
        {matchedLines.length > 0 ? (
          <div>
            <div className="invoice-review-table-head" aria-hidden="true">
              <span>Item</span><span>Previous price</span><span>New price</span><span>Change</span><span>This invoice</span>
            </div>
            {matchedLines.map((l) => {
              const idx = lines.indexOf(l);
              const accepted = l.accepted !== false;
              const hasPrevious = Number.isFinite(Number(l.oldPrice)) && Number(l.oldPrice) > 0;
              const isIncrease = hasPrevious && Number(l.price) > Number(l.oldPrice);
              const isDecrease = hasPrevious && Number(l.price) < Number(l.oldPrice);
              return (
                <div key={idx} className="invoice-review-matched-row" style={{ opacity: accepted ? 1 : 0.6 }}>
                  <div className="invoice-review-item-cell">
                    <div style={{ fontWeight: 750, color: C.text, fontSize: '0.86rem' }}>
                      {l.existingDescription || l.name || '—'}
                      {l.taxable === true && <TaxedPill style={{ marginLeft: '0.4rem' }} />}
                    </div>
                    <div style={{ color: '#6B5A52', fontSize: '0.74rem', marginTop: '0.18rem' }}>{[l.vendor || vendor, l.unit || 'Package not listed'].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="invoice-review-cell">
                    <span className="invoice-review-mobile-label">Previous</span>
                    <span style={{ color: '#7A6A62', fontSize: '0.82rem', textDecoration: hasPrevious && Number(l.price) !== Number(l.oldPrice) ? 'line-through' : 'none' }}>{hasPrevious ? fmtMoney(l.oldPrice) : '—'}</span>
                  </div>
                  <div className="invoice-review-cell">
                    <span className="invoice-review-mobile-label">New</span>
                    <strong style={{ color: C.text, fontSize: '0.9rem' }}>{fmtMoney(l.price)}</strong>
                  </div>
                  <div className="invoice-review-cell">
                    <span className="invoice-review-mobile-label">Change</span>
                    <span style={{ color: isIncrease ? '#A14E32' : isDecrease ? '#2F6B32' : C.text, fontSize: '0.78rem', fontWeight: 700 }}>{priceChangeLabel(l.oldPrice, l.price)}</span>
                  </div>
                  <div className="invoice-review-cell">
                    <span className="invoice-review-mobile-label">This invoice</span>
                    <AcceptToggle
                      accepted={accepted}
                      onChange={(next) => setLineAccepted(idx, next)}
                      ariaLabel={`Accept or ignore ${l.existingDescription || l.name || 'existing item'}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: C.muted, padding: '1rem' }}>No existing items found on this invoice.</p>
        )}
      </section>

      {newLines.length > 0 && (
        <section style={{ ...card, padding: 0, overflow: 'hidden', background: '#FFFCF5' }} aria-labelledby="new-items-heading">
          <div style={{ padding: '1rem 1rem 0.85rem', borderBottom: '1px solid #EFE3C4' }}>
            <h4 id="new-items-heading" style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '1rem', fontWeight: 800, margin: 0 }}>New items</h4>
            <p style={{ color: '#6B5A52', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>These items are not currently in your catalog and will be added when you approve.</p>
            <SectionSelectControls
              acceptedCount={acceptedNew.length}
              totalCount={newLines.length}
              onSelectAll={() => setSectionAccepted(isNewLine, true)}
              onDeselectAll={() => setSectionAccepted(isNewLine, false)}
              label="Ignored lines are not added to the catalog this time."
            />
          </div>
          <div style={{ display: 'grid', gap: '0.65rem', padding: '0.85rem' }}>
            {newLines.map((l) => {
              const idx = lines.indexOf(l);
              const accepted = l.accepted !== false;
              return (
                <div key={idx} style={{ background: '#fff', border: `1px solid ${accepted ? '#E9DEC2' : '#E8DDD0'}`, borderRadius: 10, padding: '0.85rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', opacity: accepted ? 1 : 0.6 }}>
                  <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                    <div style={{ fontWeight: 750, color: C.text, fontSize: '0.86rem' }}>{l.name || 'Unnamed invoice item'}</div>
                    <div style={{ color: '#6B5A52', fontSize: '0.74rem', marginTop: '0.18rem' }}>{[l.vendor || vendor, l.unit || 'Package not listed'].filter(Boolean).join(' · ')}</div>
                    <div style={{ color: C.text, fontSize: '0.78rem', marginTop: '0.5rem' }}>Invoice price: <strong>{fmtMoney(l.price)}</strong></div>
                  </div>
                  <div style={{ flex: '0 1 280px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                      {l.category && (
                        <span style={{ display: 'inline-block', color: '#2F5D3A', background: '#E3F0E4', border: '1px solid #B9DBBE', borderRadius: 999, padding: '0.22rem 0.55rem', fontSize: '0.68rem', fontWeight: 800 }}>{l.category}</span>
                      )}
                      <span style={{ display: 'inline-block', color: '#7A5A0A', background: '#FFF0BD', border: '1px solid #E6CD83', borderRadius: 999, padding: '0.22rem 0.55rem', fontSize: '0.68rem', fontWeight: 800 }}>New to catalog</span>
                      {l.taxable === true && <TaxedPill />}
                    </div>
                    <AcceptToggle
                      accepted={accepted}
                      onChange={(next) => setLineAccepted(idx, next)}
                      ariaLabel={`Accept or ignore ${l.name || 'new item'}`}
                    />
                    <div style={{ color: '#6B5A52', fontSize: '0.72rem', lineHeight: 1.4, marginTop: '0.4rem' }}>
                      {accepted
                        ? (l._linkMode === 'pin'
                          ? 'Will link to the matched item when you approve — no duplicate item is created.'
                          : (l.category ? `Suggested category: ${l.category}. ` : '') + 'Will be added when you approve.')
                        : 'Ignored for this invoice only. It can appear again on a future import.'}
                    </div>
                    <CrossReferenceLinkControl line={l} onChange={(patch) => setLineLink(idx, patch)} />
                    {l.potentialMatch && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#FFF7E6', border: '1px solid #E6CD83', borderRadius: 8 }}>
                        <div style={{ color: '#7A5A0A', fontSize: '0.72rem', fontWeight: 800 }}>
                          Possible match{l.potentialMatch.confidencePct != null ? ` · ${l.potentialMatch.confidencePct}% confident` : ''}
                        </div>
                        <div style={{ color: '#6B5A52', fontSize: '0.72rem', marginTop: '0.2rem', lineHeight: 1.4 }}>
                          {[l.potentialMatch.existingDescription || 'an existing item', l.potentialMatch.existingVendor, l.potentialMatch.oldPrice != null ? `was ${fmtMoney(l.potentialMatch.oldPrice)}` : null].filter(Boolean).join(' · ')}
                        </div>
                        <button
                          type="button"
                          onClick={() => mapPotentialMatch(idx)}
                          style={{ ...btn('secondary'), marginTop: '0.45rem', padding: '0.3rem 0.6rem', fontSize: '0.72rem' }}
                        >
                          Map to this item
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!lines.length && <div style={card}><p style={{ color: C.muted, margin: 0 }}>No line items were extracted from this invoice.</p></div>}

      <section style={{ ...card }} aria-labelledby="invoice-totals-heading">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h4 id="invoice-totals-heading" style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '1rem', fontWeight: 800, margin: 0 }}>Invoice totals</h4>
          <span style={{ color: '#6B5A52', fontSize: '0.74rem' }}>Line items total {fmtMoney(computedLineTotal)}</span>
        </div>
        <p style={{ color: '#6B5A52', fontSize: '0.78rem', margin: '0.25rem 0 0.85rem' }}>
          Captured from the invoice. Tax, delivery, and other charges are recorded separately from item costs. Correct anything the reader misread before you approve.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.65rem' }}>
          {TOTAL_FIELDS.map(([field, label]) => (
            <label key={field} style={{ display: 'block' }}>
              <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>{label}</span>
              <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${C.border}`, borderRadius: 7, padding: '0 0.5rem', background: '#fff' }}>
                <span style={{ color: '#9A8A80', fontSize: '0.82rem' }}>$</span>
                <input
                  type="number" inputMode="decimal" step="0.01"
                  value={totals && totals[field] != null ? totals[field] : ''}
                  onChange={(e) => setTotalField(field, e.target.value)}
                  aria-label={label}
                  placeholder="—"
                  style={{ border: 'none', outline: 'none', width: '100%', padding: '0.4rem 0.35rem', fontSize: '0.86rem', color: C.text, background: 'transparent', fontWeight: field === 'total' ? 800 : 500 }}
                />
              </div>
            </label>
          ))}
        </div>
        <div style={{ marginTop: '1rem', borderTop: `1px solid ${C.border}`, paddingTop: '0.85rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ color: C.text, fontSize: '0.88rem', fontWeight: 800 }}>Additional charges</div>
            <button
              type="button"
              onClick={addCharge}
              style={{ background: 'none', border: 'none', padding: 0, color: C.text, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}
            >
              + Add charge
            </button>
          </div>
          <p style={{ color: '#6B5A52', fontSize: '0.76rem', margin: '0.25rem 0 0.35rem', lineHeight: 1.45 }}>
            Non-product charges and credits itemized from this invoice — recorded with the invoice, never folded into item prices. Enter credits as positive amounts.
          </p>
          {charges.length === 0 ? (
            <p style={{ color: C.faint, fontSize: '0.78rem', margin: '0.35rem 0 0' }}>No additional charges detected on this invoice.</p>
          ) : (
            charges.map((c, i) => (
              <div key={i} className="invoice-review-charge-row">
                <select
                  value={c.type}
                  onChange={(e) => setChargeField(i, 'type', e.target.value)}
                  aria-label={`Charge ${i + 1} type`}
                  style={{ border: `1px solid ${C.border}`, borderRadius: 7, padding: '0.4rem 0.4rem', fontSize: '0.82rem', color: C.text, background: '#fff', minWidth: 0 }}
                >
                  {INVOICE_CHARGE_TYPES.map((t) => (
                    <option key={t} value={t}>{CHARGE_TYPE_LABELS[t]}</option>
                  ))}
                </select>
                <input
                  value={c.label}
                  onChange={(e) => setChargeField(i, 'label', e.target.value)}
                  aria-label={`Charge ${i + 1} label`}
                  placeholder="As printed on the invoice"
                  style={{ border: `1px solid ${C.border}`, borderRadius: 7, padding: '0.4rem 0.5rem', fontSize: '0.82rem', color: C.text, background: '#fff', minWidth: 0 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${C.border}`, borderRadius: 7, padding: '0 0.5rem', background: '#fff' }}>
                  <span style={{ color: '#9A8A80', fontSize: '0.82rem' }}>$</span>
                  <input
                    type="number" inputMode="decimal" step="0.01"
                    value={c.amount}
                    onChange={(e) => setChargeField(i, 'amount', e.target.value)}
                    aria-label={`Charge ${i + 1} amount`}
                    placeholder="—"
                    style={{ border: 'none', outline: 'none', width: '100%', padding: '0.4rem 0.35rem', fontSize: '0.82rem', color: C.text, background: 'transparent' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeCharge(i)}
                  aria-label={`Remove charge ${i + 1}`}
                  style={{ background: 'none', border: 'none', padding: 0, color: C.muted, fontWeight: 800, fontSize: '1.05rem', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
        {showReconcile && (
          reconcile.explained ? (
            <div style={{ marginTop: '0.75rem', background: '#F1F8EE', border: '1px solid #CFE3BE', borderRadius: 8, padding: '0.6rem 0.75rem', color: '#456A2F', fontSize: '0.78rem', lineHeight: 1.45 }}>
              Printed total {fmtMoney(printedTotal)} is {fmtMoney(Math.abs(reconcileDiff))} {reconcileDiff >= 0 ? 'above' : 'below'} the line items total {fmtMoney(computedLineTotal)} — this matches the detected charges{explainedChargeNames ? `: ${explainedChargeNames}` : ''}. The figures are kept as recorded.
            </div>
          ) : (
            <div style={{ marginTop: '0.75rem', background: '#FFF7E6', border: '1px solid #F0DBA8', borderRadius: 8, padding: '0.6rem 0.75rem', color: '#7A5A0A', fontSize: '0.78rem', lineHeight: 1.45 }}>
              Printed total {fmtMoney(printedTotal)} differs from the line items total {fmtMoney(computedLineTotal)} by {fmtMoney(Math.abs(reconcileDiff))}. This is normal when tax or fees are folded into the total — the figures are kept as recorded.
            </div>
          )
        )}
      </section>

      {vendorFeePlan.length > 0 && (
        <section style={{ ...card }} aria-labelledby="vendor-fees-heading">
          <h4 id="vendor-fees-heading" style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '1rem', fontWeight: 800, margin: 0 }}>
            Save to {vendor}?
          </h4>
          <p style={{ color: '#6B5A52', fontSize: '0.78rem', margin: '0.25rem 0 0.75rem', lineHeight: 1.45 }}>
            Countertop read these fee settings off this invoice. Checked entries are saved to the vendor when you approve — they fill only blank settings, and your own edits always win.
          </p>
          {vendorFeePlan.map((entry, i) => (
            <label key={`${entry.column}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.4rem 0', borderTop: i === 0 ? 'none' : `1px dashed ${C.border}`, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={feeAccepted[i] !== false}
                onChange={() => toggleFeeAccepted(i)}
                aria-label={`Save ${feePlanEntryLabel(entry)} to ${vendor}`}
                style={{ marginTop: '0.15rem' }}
              />
              <span style={{ color: C.text, fontSize: '0.82rem', lineHeight: 1.45 }}>
                <strong>{feePlanEntryLabel(entry)} {feePlanValueText(entry)}</strong>
                {' '}— fills only blank settings; edit anytime in Vendors.
                {entry.label ? <span style={{ color: C.faint }}>{` Printed as “${entry.label}”.`}</span> : null}
              </span>
            </label>
          ))}
        </section>
      )}

      <div style={{ ...card, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ color: '#6B5A52', fontSize: '0.78rem', marginRight: 'auto', lineHeight: 1.45 }}>
          {!invoiceDate && (priceUpdateCount > 0 || acceptedNew.length > 0)
            ? `No invoice date — approving records the purchase${acceptedNew.length > 0 ? ` and adds ${acceptedNew.length} new item${acceptedNew.length === 1 ? '' : 's'}` : ''}; existing items keep their current prices.`
            : acceptedNew.length > 0
              ? `Approving adds ${acceptedNew.length} new item${acceptedNew.length === 1 ? '' : 's'} and applies ${priceUpdateCount} price update${priceUpdateCount === 1 ? '' : 's'}.`
              : priceUpdateCount > 0
                ? `Approving applies ${priceUpdateCount} price update${priceUpdateCount === 1 ? '' : 's'}.`
                : 'No catalog changes selected — you can still approve to finish this invoice.'}
          {(matchedLines.length - acceptedMatched.length + newLines.length - acceptedNew.length) > 0 && (
            <span> {matchedLines.length - acceptedMatched.length + newLines.length - acceptedNew.length} line{(matchedLines.length - acceptedMatched.length + newLines.length - acceptedNew.length) === 1 ? '' : 's'} ignored for this review only.</span>
          )}
        </div>
        <button className="invoice-review-approval" onClick={approve} disabled={!!busy} aria-label="Approve invoice price updates and continue" style={{ ...btn('primary'), padding: '0.7rem 1.25rem', fontSize: '0.92rem', opacity: busy ? 0.65 : 1 }}>
          {busy ? 'Applying updates…' : 'Approve & Continue'}
        </button>
      </div>

      {datePrompt && (
        <Modal onClose={busy ? undefined : () => setDatePrompt(false)} maxWidth={520}>
          <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.15rem', marginBottom: '0.25rem' }}>
            Update prices with this invoice date?
          </h3>
          <p style={{ color: C.muted, fontSize: '0.85rem', lineHeight: 1.55, marginBottom: '1rem' }}>
            You set the invoice date to <strong style={{ color: C.text }}>{fmtDate(invoiceDate)}</strong>.
            {' '}Matched items take this invoice's prices only when that date is newer than the
            price already on file — older invoices are recorded as purchases without changing prices.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              disabled={!!busy}
              onClick={() => { setDatePrompt(false); submitApproval(false); }}
              style={btn('secondary')}
            >
              Record without price updates
            </button>
            <button
              disabled={!!busy}
              onClick={() => { setDatePrompt(false); submitApproval(true); }}
              style={btn('primary')}
            >
              Update prices
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Invoice row ───────────────────────────────────────────────────────────────

function InvoiceRow({ row, onReview, onPreview, onAction, actionBusy }) {
  const vendor = row.vendor_name || 'Vendor not identified';
  const tab = tabForStatus(row.status);
  const counts = row.status === 'processed' || row.status === 'partial'
    ? resultCountsLine(row.result)
    : countsLine(row.proposal?.counts);
  const busy = actionBusy === row.id;
  // Aligned "date column": lead with the invoice's own date when we have it,
  // otherwise the date it was received. Order across the row is date → vendor →
  // invoice number, so the list scans like a table.
  const dateValue = row.invoice_date || row.received_at;
  const dateLabel = row.invoice_date ? 'Invoice date' : (row.received_at ? 'Received' : '');
  const canPreview = !!row.invoice_id && (row.status === 'processed' || row.status === 'partial');
  return (
    <div style={{ padding: '0.85rem 0.5rem', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flex: '1 1 260px', minWidth: 0 }}>
          {/* Date column */}
          <div style={{ minWidth: 56, flexShrink: 0 }}>
            <div style={{ fontWeight: 800, color: C.text, fontSize: '0.82rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {fmtDateShort(dateValue) || '—'}
            </div>
            {dateLabel && <div style={{ color: C.faint, fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{dateLabel}</div>}
          </div>
          {/* Vendor + invoice number */}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, color: C.text, fontSize: '0.95rem' }}>{vendor}</span>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.68rem',
                fontWeight: 700, color: '#fff', background: statusColor(row.status), whiteSpace: 'nowrap',
              }}>
                {statusLabel(row)}
              </span>
            </div>
            <div style={{ color: C.muted, fontSize: '0.78rem', marginTop: '0.15rem' }}>
              {[
                row.invoice_number ? `Invoice #${row.invoice_number}` : 'No invoice number',
                row.batch_total > 1 ? `${row.batch_index || 1} of ${row.batch_total} in this email` : null,
              ].filter(Boolean).join(' · ')}
            </div>
            {counts && <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.1rem' }}>{counts}</div>}
            {row.status === 'needs_review' && !row.invoice_date && (
              <div style={{ color: C.amber, fontSize: '0.75rem', fontWeight: 700, marginTop: '0.1rem' }}>
                Invoice date was not found — set it in review to enable price updates.
              </div>
            )}
            {row.status === 'failed' && row.error && (
              <div style={{ color: C.red, fontSize: '0.75rem', marginTop: '0.1rem' }}>Error: {row.error}</div>
            )}
            {row.status === 'rejected' && REJECT_EXPLANATIONS[row.reject_reason] && (
              <div style={{ color: C.muted, fontSize: '0.75rem', marginTop: '0.1rem', lineHeight: 1.4, maxWidth: 520 }}>
                {REJECT_EXPLANATIONS[row.reject_reason]}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {tab === 'needs_review' && (
            <button onClick={() => onReview(row.id)} style={btn('primary')}>Review Invoice</button>
          )}
          {tab === 'completed' && (
            <>
              {canPreview && (
                <button onClick={() => onPreview(row.invoice_id)} style={btn('secondary')}>View / Edit</button>
              )}
              <button onClick={() => onReview(row.id)} style={btn('secondary')}>View Changes</button>
              <button onClick={() => { window.location.href = '/items.html'; }} style={btn('secondary')}>View Items in Catalog</button>
            </>
          )}
          {tab === 'not_processed' && (
            <>
              <button disabled={busy} onClick={() => onAction('review_anyway', row.id)} style={btn('primary')}>
                {busy ? '⏳' : 'Review Anyway'}
              </button>
              {row.status !== 'rejected' && (
                <button disabled={busy} onClick={() => onAction('mark_not_invoice', row.id)} style={btn('secondary')}>Mark as Not an Invoice</button>
              )}
              <button disabled={busy} onClick={() => onAction('delete_email', row.id)} style={btn('danger')}>Delete</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Completed-invoice change viewer ("View Changes") ─────────────────────────

function CompletedView({ emailId, onBack }) {
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    callProcessor({ action: 'get_invoice', emailId })
      .then((d) => setInvoice(d.invoice))
      .catch((e) => setError(e.message));
  }, [emailId]);
  if (error) return <div style={card}><p style={{ color: C.red }}>{error}</p><button onClick={onBack} style={btn('secondary')}>← Back</button></div>;
  if (!invoice) return <div style={card}><p style={{ color: C.muted }}>Loading…</p></div>;
  const r = invoice.result || {};
  const section = (label, rows, render) => rows?.length ? (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ fontWeight: 700, color: C.text, fontSize: '0.9rem', marginBottom: '0.3rem' }}>{label}</div>
      {rows.map((it, i) => (
        <div key={i} style={{ fontSize: '0.82rem', color: C.muted, padding: '0.2rem 0' }}>{render(it)}</div>
      ))}
    </div>
  ) : null;
  return (
    <div style={card}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.75rem' }}>
        ← Back to Invoice Inbox
      </button>
      <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.15rem', marginBottom: '0.25rem' }}>
        {invoice.vendor_name || 'Vendor not identified'} — invoice processed
      </h3>
      <p style={{ color: C.muted, fontSize: '0.85rem' }}>
        {resultCountsLine(r) || 'No changes were applied.'}
        {invoice.approval_method && (
          <span style={{ color: C.faint }}>
            {' '}· {invoice.approval_method === 'automatic_setup' ? 'Applied by Automatic Setup'
              : invoice.approval_method === 'organize' ? 'Approved — Countertop organized everything'
              : 'Approved — items added to catalog only'}
            {invoice.approved_at ? ` on ${fmtDate(invoice.approved_at)}` : ''}
          </span>
        )}
      </p>
      {section('Price updates', r.updatedDetails, (it) => `${it.description}: ${fmtMoney(it.oldPrice)} → ${fmtMoney(it.newPrice)}`)}
      {section('New items added', r.addedDetails, (it) => `${it.description}${it.case_price != null ? ` — ${fmtMoney(it.case_price)}` : ''}${it.vendor ? ` (${it.vendor})` : ''}`)}
      {section('Attached to existing items', r.attachedDetails, (it) => `${it.matchedItem || it.description} (${[it.vendor, it.description, it.unit].filter(Boolean).join(' · ')})`)}
      {section('New vendors', r.newVendors, (v) => v)}
      {section('Counts & templates', r.templates, (t) => t)}
      {section('Errors', r.errors, (e) => e)}
      <div style={{ marginTop: '1rem' }}>
        <button onClick={() => { window.location.href = '/items.html'; }} style={btn('primary')}>View Items in Catalog</button>
      </div>
    </div>
  );
}

// ── View & Edit tab + invoice editor ─────────────────────────────────────────

const searchInputStyle = {
  padding: '0.5rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: 8,
  fontSize: '0.86rem', color: C.text, background: '#fff', width: '100%',
};

const SORT_OPTIONS = [
  { key: 'date', label: 'Date' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'amount', label: 'Amount' },
];
const DEFAULT_DIRECTION = { date: 'desc', vendor: 'asc', amount: 'desc' };
const EMPTY_FILTERS = { vendor: '', invoiceNumber: '', item: '', startDate: '', endDate: '' };
const LIBRARY_PAGE = 50;

// Source chips are scanned across many rows, so they share one fixed width.
const SOURCE_CHIP_COLORS = {
  email: { color: '#456A2F', background: '#F1F8EE', border: '#CFE3BE' },
  scan: { color: '#456A2F', background: '#F1F8EE', border: '#CFE3BE' },
  import: { color: '#6B5A52', background: C.chip, border: '#D4C4B0' },
  trip: { color: C.purple, background: '#F4F1F8', border: '#D9D1E6' },
};

function SourceChip({ sourceKey, label }) {
  const palette = SOURCE_CHIP_COLORS[sourceKey] || SOURCE_CHIP_COLORS.import;
  return (
    <span style={{
      display: 'inline-block', width: 128, textAlign: 'center', boxSizing: 'border-box',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      borderRadius: 999, padding: '0.2rem 0.5rem', fontSize: '0.66rem', fontWeight: 800,
      color: palette.color, background: palette.background, border: `1px solid ${palette.border}`,
    }}>
      {label || 'Recorded'}
    </span>
  );
}

// ── View & Edit tab: every recorded invoice ───────────────────────────────────
// Lists store_invoices directly — emailed, scanned, shopping-trip, and the
// purchases recorded while importing items (which never had an inbox row and
// so never appeared on the queue tabs). Sort by date / vendor / amount; recall
// an invoice by vendor, number, date, or an item on it; open one to correct it.

function InvoiceLibrary({ storeId, onOpen, onTotal, refreshKey = 0 }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('date');
  const [direction, setDirection] = useState('desc');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const runSearch = useCallback(async ({ f, s, d, append = false } = {}) => {
    if (!storeId) return;
    const useFilters = f || filters;
    const useSort = s || sort;
    const useDirection = d || direction;
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const data = await callProcessor({
        action: 'search_invoices', storeId, ...useFilters,
        sort: useSort, direction: useDirection,
        limit: LIBRARY_PAGE, offset: append ? results.length : 0,
      });
      const rows = Array.isArray(data.invoices) ? data.invoices : [];
      setResults((prev) => (append ? [...prev, ...rows] : rows));
      const count = Number.isFinite(Number(data.total)) ? Number(data.total) : rows.length;
      setTotal(count);
      onTotal?.(count);
      setSearched(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [storeId, filters, sort, direction, results.length, onTotal]);

  // Show the most recent invoices on open, and again after an edit is saved.
  useEffect(() => { runSearch({}); /* eslint-disable-next-line */ }, [storeId, refreshKey]);

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => { setFilters(EMPTY_FILTERS); runSearch({ f: EMPTY_FILTERS }); };
  const chooseSort = (key) => {
    // Re-choosing the active sort flips its direction; a new sort starts at
    // its natural direction (newest / largest first, vendors A→Z).
    const next = key === sort ? (direction === 'asc' ? 'desc' : 'asc') : DEFAULT_DIRECTION[key];
    setSort(key);
    setDirection(next);
    runSearch({ s: key, d: next });
  };
  const arrow = (key) => (key === sort ? (direction === 'asc' ? ' ↑' : ' ↓') : '');
  const itemTerm = filters.item.trim();

  return (
    <div>
      <p style={{ color: C.muted, fontSize: '0.85rem', margin: '0.75rem 0 0.9rem', lineHeight: 1.5 }}>
        Every invoice on record — emailed, scanned, or recorded while importing items. Find one by vendor,
        invoice number, date, or an item on it, then open it to correct a quantity or price.
      </p>
      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.65rem', alignItems: 'end', marginBottom: '0.9rem' }}
      >
        <label>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Item</span>
          <input value={filters.item} onChange={(e) => setF('item', e.target.value)} placeholder="e.g. whole milk" aria-label="Item" style={searchInputStyle} />
        </label>
        <label>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Vendor</span>
          <input value={filters.vendor} onChange={(e) => setF('vendor', e.target.value)} placeholder="e.g. Sysco" aria-label="Vendor" style={searchInputStyle} />
        </label>
        <label>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Invoice #</span>
          <input value={filters.invoiceNumber} onChange={(e) => setF('invoiceNumber', e.target.value)} placeholder="e.g. 1042" aria-label="Invoice number" style={searchInputStyle} />
        </label>
        <label>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>From date</span>
          <input type="date" value={filters.startDate} onChange={(e) => setF('startDate', e.target.value)} aria-label="From date" style={searchInputStyle} />
        </label>
        <label>
          <span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>To date</span>
          <input type="date" value={filters.endDate} onChange={(e) => setF('endDate', e.target.value)} aria-label="To date" style={searchInputStyle} />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" disabled={loading} style={{ ...btn('primary'), opacity: loading ? 0.6 : 1 }}>{loading ? 'Searching…' : 'Search'}</button>
          <button type="button" onClick={clearFilters} disabled={loading} style={btn('secondary')}>Clear</button>
        </div>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <span style={{ color: '#6B5A52', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Sort by</span>
        {SORT_OPTIONS.map((o) => {
          const active = o.key === sort;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => chooseSort(o.key)}
              aria-pressed={active}
              aria-label={`Sort by ${o.label.toLowerCase()}`}
              style={{
                ...btn('secondary'), padding: '0.3rem 0.7rem', fontSize: '0.78rem', width: 96, textAlign: 'center',
                background: active ? C.text : '#fff', color: active ? '#fff' : C.text,
                borderColor: active ? C.text : '#D4C4B0',
              }}
            >
              {o.label}{arrow(o.key)}
            </button>
          );
        })}
        {total != null && searched && !loading && (
          <span style={{ marginLeft: 'auto', color: C.faint, fontSize: '0.78rem' }}>
            {total === 1 ? '1 invoice' : `${total} invoices`}
          </span>
        )}
      </div>

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        {error && <p style={{ color: C.red, fontSize: '0.85rem', padding: '1rem' }}>Couldn't load invoices: {error}</p>}
        {!error && loading && <InlineLoader label="Loading invoices…" />}
        {!error && !loading && results.length === 0 && searched && (
          <p style={{ color: C.muted, fontSize: '0.88rem', padding: '2rem 1rem', textAlign: 'center' }}>
            {itemTerm
              ? `No recorded invoice lists “${itemTerm}”. Try a shorter word, or the name printed on the invoice.`
              : 'No invoices matched. Try a broader search, or a different date range.'}
          </p>
        )}
        {!error && !loading && results.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#FCFAF8', color: '#6B5A52', textAlign: 'left' }}>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700 }}>Invoice date</th>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700 }}>Vendor</th>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700 }}>Invoice #</th>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700 }}>Source</th>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700, textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '0.65rem 0.85rem', fontWeight: 700, textAlign: 'right' }}>Items</th>
                  <th style={{ padding: '0.65rem 0.85rem' }} />
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} data-testid="invoice-library-row" style={{ borderTop: '1px solid #EFE7E0' }}>
                    <td style={{ padding: '0.6rem 0.85rem', color: C.text, whiteSpace: 'nowrap' }}>{r.invoice_date ? fmtDate(r.invoice_date) : '—'}</td>
                    <td style={{ padding: '0.6rem 0.85rem', color: C.text, fontWeight: 650 }}>
                      {r.vendor_name || '—'}
                      {r.matched_lines?.length > 0 && (
                        <div style={{ color: C.faint, fontSize: '0.74rem', fontWeight: 500, marginTop: '0.15rem' }}>
                          Has: {r.matched_lines.join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.6rem 0.85rem', color: '#6B5A52' }}>{r.invoice_number || '—'}</td>
                    <td style={{ padding: '0.6rem 0.85rem' }}><SourceChip sourceKey={r.source_key} label={r.source_label} /></td>
                    <td style={{ padding: '0.6rem 0.85rem', color: C.text, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {r.total_amount != null ? fmtMoney(r.total_amount) : (r.computed_line_total != null ? fmtMoney(r.computed_line_total) : '—')}
                    </td>
                    <td style={{ padding: '0.6rem 0.85rem', color: '#6B5A52', textAlign: 'right' }}>{r.line_count ?? '—'}</td>
                    <td style={{ padding: '0.6rem 0.85rem', textAlign: 'right' }}>
                      <button onClick={() => onOpen(r.id)} style={{ ...btn('secondary'), padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>View / Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!error && !loading && total != null && results.length < total && (
          <div style={{ padding: '0.6rem 0.85rem', borderTop: '1px solid #EFE7E0', textAlign: 'center' }}>
            <button type="button" disabled={loadingMore} onClick={() => runSearch({ append: true })} style={btn('secondary')}>
              {loadingMore ? 'Loading…' : `Show more (${results.length} of ${total})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const EDITOR_MONEY_FIELDS = [
  ['subtotal_amount', 'Subtotal'],
  ['tax_amount', 'Tax'],
  ['delivery_amount', 'Delivery'],
  ['other_charges_amount', 'Other charges'],
  ['discount_amount', 'Discount'],
  ['total_amount', 'Invoice total'],
];

// Non-item line rows (line_kind tax/delivery/fee/discount/other) are records of
// the invoice's own charges, never products — they carry no category and are
// excluded from computed_line_total and purchase totals. Badge text prefers the
// fine charge_type ("Fuel surcharge") over the coarse line_kind ("Fee").
const LINE_KIND_LABELS = {
  tax: 'Tax',
  delivery: 'Delivery',
  fee: 'Fee',
  discount: 'Discount',
  other: 'Other charge',
};

function isChargeLine(line) {
  return !!line.line_kind && line.line_kind !== 'item';
}

function chargeKindLabel(line) {
  return CHARGE_TYPE_LABELS[line.charge_type] || LINE_KIND_LABELS[line.line_kind] || 'Charge';
}

const round2 = (n) => Math.round(n * 100) / 100;

// What a save did beyond the record itself, in the user's terms.
function cascadeSummaryText(cascade) {
  if (!cascade) return '';
  const parts = [];
  const updated = cascade.updated?.length || 0;
  const kept = cascade.kept?.length || 0;
  if (updated) parts.push(`${updated} catalog price${updated === 1 ? '' : 's'} updated`);
  if (kept) parts.push(`${kept} catalog price${kept === 1 ? '' : 's'} kept — a newer invoice already set ${kept === 1 ? 'it' : 'them'}`);
  if (cascade.historyAmended) parts.push(`price history corrected for ${cascade.historyAmended}`);
  return parts.join(' · ');
}

function InvoiceEditor({ invoiceId, onBack, onSaved, readOnly = false, backLabel = '← Back to invoices' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [cascade, setCascade] = useState(null);

  const hydrate = (inv, lns) => {
    setHeader({
      source_label: inv.source_label ?? '',
      vendor_name: inv.vendor_name ?? '',
      invoice_number: inv.invoice_number ?? '',
      invoice_date: inv.invoice_date ?? '',
      due_date: inv.due_date ?? '',
      payment_terms: inv.payment_terms ?? '',
      computed_line_total: inv.computed_line_total,
      subtotal_amount: inv.subtotal_amount ?? '',
      tax_amount: inv.tax_amount ?? '',
      delivery_amount: inv.delivery_amount ?? '',
      other_charges_amount: inv.other_charges_amount ?? '',
      discount_amount: inv.discount_amount ?? '',
      total_amount: inv.total_amount ?? '',
    });
    setLines((lns || []).map((l) => ({ ...l })));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await callProcessor({ action: 'get_invoice_detail', invoiceId });
        if (!alive) return;
        hydrate(data.invoice || {}, data.lines || []);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [invoiceId]);

  const setH = (key, value) => { setHeader((prev) => ({ ...prev, [key]: value })); setSavedMsg(''); setCascade(null); };
  // A quantity or unit-cost fix carries the line total with it (qty × unit
  // cost) — the figure the category purchase totals read — unless the user
  // then edits the total directly.
  const setL = (idx, key, value) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [key]: value };
      if (key === 'quantity' || key === 'unit_cost') {
        const q = parseFloat(next.quantity);
        const c = parseFloat(next.unit_cost);
        if (Number.isFinite(q) && Number.isFinite(c)) next.extended_amount = round2(q * c);
      }
      return next;
    }));
    setSavedMsg('');
    setCascade(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const headerPayload = {
        vendor_name: header.vendor_name,
        invoice_number: header.invoice_number,
        invoice_date: header.invoice_date || '',
        due_date: header.due_date || '',
        payment_terms: header.payment_terms,
      };
      EDITOR_MONEY_FIELDS.forEach(([col]) => { headerPayload[col] = header[col]; });
      const linePayload = lines.map((l) => ({
        id: l.id,
        description: l.description,
        category_snapshot: l.category_snapshot,
        quantity: l.quantity,
        unit_cost: l.unit_cost,
        extended_amount: l.extended_amount,
      }));
      const data = await callProcessor({ action: 'update_invoice', invoiceId, header: headerPayload, lines: linePayload });
      if (data.invoice) hydrate(data.invoice, data.lines || []);
      setSavedMsg('Saved.');
      setCascade(data.cascade || null);
      onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={card}><p style={{ color: C.muted }}>Loading invoice…</p></div>;
  if (error && !header) return (
    <div style={card}>
      <p style={{ color: C.red }}>Couldn't load this invoice: {error}</p>
      <button onClick={onBack} style={btn('secondary')}>{backLabel}</button>
    </div>
  );

  const h = header || {};
  const cellInput = { ...searchInputStyle, padding: '0.35rem 0.45rem', fontSize: '0.82rem' };

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.75rem' }}>
        {backLabel}
      </button>

      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, fontSize: '1.2rem', margin: 0 }}>{readOnly ? 'Invoice details' : 'Edit invoice'}</h3>
          <span style={{ color: '#6B5A52', fontSize: '0.74rem' }}>
            {h.source_label ? `${h.source_label} · ` : ''}Line items total {fmtMoney(h.computed_line_total)}
          </span>
        </div>
        <p style={{ color: C.muted, fontSize: '0.8rem', margin: '0.25rem 0 1rem', lineHeight: 1.5, maxWidth: 720 }}>
          {readOnly
            ? 'A read-only view of what was captured from this invoice. Open it from the View & Edit tab to make corrections.'
            : 'Corrections fix what was read from this invoice — they are not a new purchase or a new price event. A quantity or line-total fix updates this purchase record and its category totals. A unit-cost fix on a line linked to a catalog item also updates that item’s price, vendor product, and price history as if the corrected price had been on the invoice; a newer invoice’s price is never overwritten.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.65rem' }}>
          <label><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Vendor</span>
            <input value={h.vendor_name} onChange={(e) => setH('vendor_name', e.target.value)} style={searchInputStyle} /></label>
          <label><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Invoice #</span>
            <input value={h.invoice_number} onChange={(e) => setH('invoice_number', e.target.value)} style={searchInputStyle} /></label>
          <label><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Invoice date</span>
            <input type="date" value={h.invoice_date || ''} onChange={(e) => setH('invoice_date', e.target.value)} style={searchInputStyle} /></label>
          <label><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Due date</span>
            <input type="date" value={h.due_date || ''} onChange={(e) => setH('due_date', e.target.value)} style={searchInputStyle} /></label>
          <label><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>Payment terms</span>
            <input value={h.payment_terms} onChange={(e) => setH('payment_terms', e.target.value)} placeholder="e.g. Net 30" style={searchInputStyle} /></label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.65rem', marginTop: '0.75rem' }}>
          {EDITOR_MONEY_FIELDS.map(([col, label]) => (
            <label key={col}><span style={{ display: 'block', color: '#6B5A52', fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.2rem' }}>{label}</span>
              <input type="number" inputMode="decimal" step="0.01" value={h[col]} onChange={(e) => setH(col, e.target.value)} style={searchInputStyle} /></label>
          ))}
        </div>
      </div>

      <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '0.85rem 1rem', background: '#FCFAF8' }}>
          <h4 style={{ fontFamily: "'Montserrat', sans-serif", color: C.text, fontSize: '0.98rem', fontWeight: 800, margin: 0 }}>Line items</h4>
          <p style={{ color: '#6B5A52', fontSize: '0.78rem', margin: '0.2rem 0 0' }}>
            {readOnly
              ? 'The line items captured from this invoice, with the category each purchase totals into.'
              : 'Correct a quantity received, unit cost, line total, or category. Category drives which purchase bucket a line totals into; the catalog item under a line is what a unit-cost fix reaches.'}
            {lines.some(isChargeLine) && ' Badged rows are the invoice’s own charges (tax, delivery, fees) — they never count toward item purchases.'}
          </p>
        </div>
        {lines.length === 0 ? (
          <p style={{ color: C.muted, fontSize: '0.85rem', padding: '1rem' }}>This invoice has no recorded line items.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: 720 }}>
              <thead>
                <tr style={{ color: '#6B5A52', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem 0.6rem', fontWeight: 700 }}>Item</th>
                  <th style={{ padding: '0.5rem 0.6rem', fontWeight: 700, width: 130 }}>Category</th>
                  <th style={{ padding: '0.5rem 0.6rem', fontWeight: 700, width: 80 }}>Qty</th>
                  <th style={{ padding: '0.5rem 0.6rem', fontWeight: 700, width: 100 }}>Unit cost</th>
                  <th style={{ padding: '0.5rem 0.6rem', fontWeight: 700, width: 110 }}>Line total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const charge = isChargeLine(l);
                  return (
                    <tr key={l.id || idx} style={{ borderTop: '1px solid #EFE7E0', background: charge ? C.chip : 'transparent' }}>
                      <td style={{ padding: '0.45rem 0.6rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {charge && (
                            <span style={{
                              display: 'inline-block', whiteSpace: 'nowrap', borderRadius: 999, padding: '0.2rem 0.55rem',
                              fontSize: '0.66rem', fontWeight: 800, color: '#6B5A52', background: '#fff', border: '1px solid #D4C4B0',
                            }}>
                              {chargeKindLabel(l)}
                            </span>
                          )}
                          <input value={l.description ?? ''} onChange={(e) => setL(idx, 'description', e.target.value)} style={{ ...cellInput, minWidth: 180 }} />
                        </div>
                        {!charge && (
                          <div style={{ color: C.faint, fontSize: '0.7rem', marginTop: '0.2rem', paddingLeft: 2 }}>
                            {l.inventory_item_id != null
                              ? `Catalog item: ${l.item_description || 'linked'}${l.catalog_case_price != null ? ` · now ${fmtMoney(l.catalog_case_price)}` : ''}`
                              : 'Not linked to a catalog item — record only'}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.45rem 0.6rem' }}>
                        {charge ? (
                          <span style={{ color: C.faint, fontSize: '0.78rem' }}>—</span>
                        ) : (
                          <input value={l.category_snapshot ?? ''} onChange={(e) => setL(idx, 'category_snapshot', e.target.value)} placeholder="—" style={cellInput} />
                        )}
                      </td>
                      <td style={{ padding: '0.45rem 0.6rem' }}>
                        <input type="number" inputMode="decimal" step="0.001" value={l.quantity ?? ''} onChange={(e) => setL(idx, 'quantity', e.target.value)} style={cellInput} />
                      </td>
                      <td style={{ padding: '0.45rem 0.6rem' }}>
                        <input type="number" inputMode="decimal" step="0.01" value={l.unit_cost ?? ''} onChange={(e) => setL(idx, 'unit_cost', e.target.value)} style={cellInput} />
                      </td>
                      <td style={{ padding: '0.45rem 0.6rem' }}>
                        <input type="number" inputMode="decimal" step="0.01" value={l.extended_amount ?? ''} onChange={(e) => setL(idx, 'extended_amount', e.target.value)} style={cellInput} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </fieldset>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {!readOnly && (
          <button onClick={save} disabled={saving} style={{ ...btn('primary'), opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save changes'}</button>
        )}
        <button onClick={onBack} disabled={saving} style={btn('secondary')}>{readOnly ? 'Back' : 'Back to invoices'}</button>
        {savedMsg && (
          <span style={{ color: C.green, fontSize: '0.85rem', fontWeight: 700 }}>
            ✓ {savedMsg}{cascadeSummaryText(cascade) ? ` ${cascadeSummaryText(cascade)}.` : ''}
          </span>
        )}
        {error && <span style={{ color: C.red, fontSize: '0.85rem' }}>{error}</span>}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InvoiceInbox() {
  const [storeId, setStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inbox, setInbox] = useState(null); // {address, storeName, mode, showModeSwitchBanner, history}
  const [tab, setTab] = useState('needs_review');
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [view, setView] = useState({ name: 'inbox' }); // inbox | review | completed | upload | edit
  const [actionBusy, setActionBusy] = useState(null);
  const [toast, setToast] = useState(null);
  // View & Edit tab: how many invoices are on record (for the tab count) and a
  // bump that re-lists them after an edit is saved.
  const [libraryTotal, setLibraryTotal] = useState(null);
  const [libraryRefresh, setLibraryRefresh] = useState(0);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('currentStore') || 'null');
      const id = stored?.id || stored?.store_id || null;
      if (id) setStoreId(id);
      else setError('No store selected. Please log in and select a store.');
    } catch (_) {
      setError('No store selected. Please log in and select a store.');
    }
  }, []);

  const load = useCallback(async () => {
    if (!storeId) return;
    setError(null);
    try {
      const data = await callProcessor({ action: 'get_inbox', storeId });
      setInbox(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  // Deep links: ?invoice=<id> from the "Invoice ready to review" notification,
  // ?template=<id> from pre-count prep cards (opens the upload flow, which
  // reads the template param itself for the completion signal).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('invoice');
    if (id) setView({ name: 'review', emailId: id });
    else if (params.get('template')) setView({ name: 'upload' });
  }, []);

  // Keep the inbox live without a manual refresh: fast polling while an email
  // is being read, slower background polling otherwise so a newly emailed
  // invoice appears on its own. Skipped while the tab is hidden; returning to
  // the tab refreshes immediately.
  useEffect(() => {
    if (view.name !== 'inbox') return undefined;
    const busy = inbox?.history?.some((r) => r.status === 'received' || r.status === 'processing');
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, busy ? 15000 : 30000);
    const onReturn = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [view.name, inbox, load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const copyAddress = async () => {
    const address = inbox?.address;
    if (!address) return;
    const onSuccess = () => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      showToast('Invoice receiving address copied');
    };
    try {
      await navigator.clipboard.writeText(address);
      onSuccess();
    } catch (_) {
      try {
        const el = document.createElement('textarea');
        el.value = address; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
        onSuccess();
      } catch (_) {}
    }
  };

  const setMode = async (mode) => {
    setSavingMode(true);
    try {
      await callProcessor({ action: 'set_mode', storeId, mode });
      setShowSettings(false);
      showToast(`Invoice handling set to ${MODE_LABELS[mode]}.`);
      await load();
    } catch (err) {
      alert(`Could not update invoice settings: ${err.message}`);
    } finally {
      setSavingMode(false);
    }
  };

  const rowAction = async (action, emailId) => {
    if (action === 'delete_email' && !window.confirm('Delete this message and its attachments? This cannot be undone.')) return;
    setActionBusy(emailId);
    try {
      await callProcessor({ action, emailId });
      if (action === 'review_anyway') setView({ name: 'review', emailId });
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionBusy(null);
    }
  };

  if (view.name === 'upload') {
    return (
      <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
        <button onClick={() => { setView({ name: 'inbox' }); load(); }}
          style={{ background: 'none', border: 'none', color: C.muted, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.75rem' }}>
          ← Back to Invoice Inbox
        </button>
        <UploadInvoiceFlow />
      </div>
    );
  }

  if (view.name === 'review') {
    return (
      <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
        <ReviewScreen emailId={view.emailId} onBack={() => setView({ name: 'inbox' })} onApplied={load} />
      </div>
    );
  }

  if (view.name === 'completed') {
    return (
      <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
        <CompletedView emailId={view.emailId} onBack={() => setView({ name: 'inbox' })} />
      </div>
    );
  }

  if (view.name === 'edit') {
    // Reached from the View & Edit tab and from a Completed row alike; the
    // page returns to whichever tab was open.
    return (
      <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
        <InvoiceEditor
          invoiceId={view.invoiceId}
          backLabel="← Back to invoices"
          onBack={() => setView({ name: 'inbox' })}
          onSaved={() => setLibraryRefresh((n) => n + 1)}
        />
      </div>
    );
  }

  const history = inbox?.history || [];
  const grouped = TABS.reduce((acc, t) => { acc[t.key] = []; return acc; }, {});
  history.forEach((r) => {
    const key = tabForStatus(r.status);
    if (key && grouped[key]) grouped[key].push(r);
  });
  // A missing/legacy mode must render the same conservative default enforced
  // by the processor: no catalog changes until the user approves.
  const mode = inbox?.mode || 'review';

  const openReview = (emailId) => {
    const row = history.find((r) => r.id === emailId);
    if (row && (row.status === 'processed' || row.status === 'partial')) setView({ name: 'completed', emailId });
    else setView({ name: 'review', emailId });
  };

  const openInvoice = (invoiceId) => setView({ name: 'edit', invoiceId });

  const emptyState = () => {
    if (tab === 'needs_review') {
      return (
        <div style={{ textAlign: 'center', padding: '2.2rem 1rem' }}>
          <div style={{ fontWeight: 800, color: C.text, fontSize: '1.05rem', marginBottom: '0.4rem' }}>
            Nothing is waiting for review
          </div>
          <p style={{ color: C.muted, fontSize: '0.88rem', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>
            New invoices will appear here after they are sent to this store's receiving address and Countertop finishes reading them.
          </p>
        </div>
      );
    }
    if (tab === 'completed') {
      return (
        <div style={{ textAlign: 'center', padding: '2.2rem 1rem' }}>
          <div style={{ fontWeight: 800, color: C.text, fontSize: '1.05rem', marginBottom: '0.4rem' }}>
            No completed invoices yet
          </div>
          <p style={{ color: C.muted, fontSize: '0.88rem', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>
            Approved or automatically processed invoices will appear here.
          </p>
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '2.2rem 1rem' }}>
        <div style={{ fontWeight: 800, color: C.text, fontSize: '1.05rem', marginBottom: '0.4rem' }}>
          No invoice issues
        </div>
        <p style={{ color: C.muted, fontSize: '0.88rem', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>
          Emails or attachments that Countertop could not recognize as invoices will appear here for you to check.
        </p>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 1100, background: C.text, color: '#fff',
          padding: '0.6rem 1rem', borderRadius: 10, fontSize: '0.85rem', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* One-time setup-complete banner */}
      {inbox?.showModeSwitchBanner && (
        <div style={{
          background: '#F1F8EE', border: '1px solid #CFE3BE', borderRadius: 12,
          padding: '0.9rem 1.1rem', marginBottom: '1.5rem', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <div style={{ color: '#456A2F', fontSize: '0.9rem', fontWeight: 600 }}>
            Your initial catalog setup is complete. New invoices will now wait for your review.
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setMode('auto_setup')} style={btn('secondary')}>Keep Automatic Setup On</button>
            <button
              onClick={async () => {
                setInbox((prev) => ({ ...prev, showModeSwitchBanner: false }));
                try { await callProcessor({ action: 'dismiss_banner', storeId }); } catch (_) {}
              }}
              style={{ background: 'none', border: 'none', color: '#456A2F', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
              aria-label="Dismiss"
            >✕</button>
          </div>
        </div>
      )}

      {/* Store invoice email card */}
      <div style={card}>
        <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, color: C.text, marginBottom: '0.35rem', fontSize: '1.2rem' }}>
          Email address for receiving invoices
        </h3>
        {inbox && !loading && (
          <p style={{ color: C.muted, fontSize: '0.88rem', margin: '0 0 0.9rem' }}>
            Send supplier invoices to this address for {inbox.storeName ? inbox.storeName : 'your store'}.
          </p>
        )}
        {loading && <p style={{ color: C.muted, fontSize: '0.85rem' }}>Loading your invoice inbox…</p>}
        {error && !loading && <p style={{ color: C.red, fontSize: '0.85rem' }}>Couldn't load the invoice inbox: {error}</p>}
        {inbox && !loading && (
          <>
            <code style={{
              display: 'block', padding: '0.85rem 1rem', background: C.chip,
              border: '1px solid #EDE3D9', borderRadius: 8, fontSize: '0.95rem', fontWeight: 700, color: C.text,
              overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal',
              marginBottom: '0.75rem', lineHeight: 1.4,
            }}>
              {inbox.address}
            </code>
            <p style={{ color: C.muted, fontSize: '0.84rem', margin: '0 0 1rem', lineHeight: 1.5, maxWidth: 640 }}>
              This is a receiving address only. Forward invoices here, give it to your suppliers, or use it as the billing email when placing orders. Do not use it to send regular messages.
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: '0.7rem', color: C.faint, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, marginBottom: '0.2rem' }}>Invoices received here are added to</div>
                <div style={{ fontWeight: 700, color: C.text, fontSize: '0.92rem' }}>{inbox.storeName || '—'}</div>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                <div style={{ fontSize: '0.7rem', color: C.faint, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, marginBottom: '0.2rem' }}>What happens after an invoice is received</div>
                <div style={{ fontWeight: 700, color: C.text, fontSize: '0.92rem', marginBottom: '0.15rem' }}>{MODE_LABELS[mode]}</div>
                <div style={{ color: C.muted, fontSize: '0.8rem', lineHeight: 1.4, maxWidth: 460 }}>{MODE_DESCRIPTIONS[mode]}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
              <button onClick={copyAddress} style={{ ...btn('primary'), background: copied ? C.green : C.text, flex: '0 1 auto', minWidth: 190 }}>
                {copied ? '✓ Copied' : 'Copy Receiving Address'}
              </button>
              <button onClick={() => setView({ name: 'upload' })} style={{ ...btn('secondary'), flex: '0 1 auto' }}>Upload Invoice</button>
              <a href="/scan-receipts.html" style={{ ...btn('secondary'), display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flex: '0 1 auto' }}>Scan receipt</a>
              <button onClick={() => setTab('invoices')} style={{ ...btn('secondary'), flex: '0 1 auto' }}>View & Edit Invoices</button>
              <button onClick={() => setShowSettings(true)} style={{ ...btn('secondary'), flex: '0 1 auto' }}>Change Handling</button>
            </div>
          </>
        )}
      </div>

      {/* How it works line */}
      {inbox && !loading && !error && (
        <div style={{
          background: '#FEFCFA', border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '0.7rem 1rem', marginBottom: '1.5rem',
          color: C.muted, fontSize: '0.84rem', lineHeight: 1.4,
        }}>
          {mode === 'review'
            ? 'How it works: Send an invoice to this address → Countertop reads and matches it → Review and approve the changes.'
            : 'How it works: Send an invoice to this address → Countertop reads it → Your catalog and count setup are updated automatically.'}
        </div>
      )}

      {/* Invoice queue */}
      <div style={{ ...card, padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, marginBottom: '0.25rem' }}>
          {TABS.map((t) => {
            const count = t.key === 'invoices' ? (libraryTotal ?? 0) : grouped[t.key].length;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '0.55rem 0.9rem', border: 'none', background: 'none', cursor: 'pointer',
                fontWeight: active ? 800 : 600, fontSize: '0.85rem',
                color: active ? C.text : C.muted,
                borderBottom: active ? `3px solid ${C.text}` : '3px solid transparent',
              }}>
                {t.label}{count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>
        {tab === 'invoices'
          ? <InvoiceLibrary storeId={storeId} onOpen={openInvoice} onTotal={setLibraryTotal} refreshKey={libraryRefresh} />
          : loading && !error
            ? <InlineLoader label="Loading invoices…" />
            : grouped[tab].length
              ? grouped[tab].map((row) => (
                <InvoiceRow key={row.id} row={row}
                  onReview={openReview} onPreview={openInvoice} onAction={rowAction} actionBusy={actionBusy} />
              ))
              : emptyState()}
      </div>

      {/* Bottom catalog link */}
      <div style={{ ...card, padding: '1rem 1.25rem' }}>
        <p style={{ color: C.muted, fontSize: '0.85rem', margin: 0 }}>
          Need to add or edit items manually?{' '}
          <a href="/items.html" style={{ color: C.text, fontWeight: 700 }}>Open Item Catalog →</a>
        </p>
      </div>

      {showSettings && (
        <SettingsModal mode={mode} saving={savingMode} onSave={setMode} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
